import { useEffect, useRef, useState } from "react";

import styles from "./LojaItem.module.scss";

import {Ellipsis,Pencil,Ban,CheckCircle,Eye,} from "lucide-react";

import { ILoja } from "shared/interfaces/ILoja";

import { useNavigate } from "react-router-dom";

import { desativarLoja } from "shared/services/api/lojas.api";

import { maskCpfCnpj } from "shared/utils/masks";

interface LojaItemProps {
  loja: ILoja;
  numeroUI: string;
  onAtualizar: () => void;
  onValidarConta: () => void;
}
export default function LojaItem({
  loja,
  numeroUI,
  onAtualizar,
  onValidarConta,
}: LojaItemProps) {
  const [menuAberto, setMenuAberto] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  /* =========================================
     STATUS DA LOJA
  ========================================= */

  const getCorStatus = () => {
    switch (loja.status) {
      case "Ativo":
        return "#DDF2E8";

      case "Pendente":
        return "#FFE6A7";

      case "Bloqueado":
        return "#FFD2D2";

      case "Inativo":
        return "#FEE2E2";

      default:
        return "#E5E7EB";
    }
  };

  /* =========================================
     STATUS KYC
  ========================================= */

  const getKycStatusLabel = () => {
    const status =
      loja.dadosBancarios?.kycStatus?.toUpperCase();

    switch (status) {
      case "APPROVED":
      case "APPROVED_KYC":
      case "VALID":
      case "VERIFIED":
        return "Conta validada";

      case "PENDING":
      case "PENDING_KYC":
        return "Validação pendente";

      case "REJECTED":
      case "REFUSED":
        return "Validação recusada";

      case "BLOCKED":
        return "Conta bloqueada";

      default:
        return "Não validada";
    }
  };

  /* =========================================
     COR DO STATUS KYC
  ========================================= */

  const getCorKycStatus = () => {
    const status =
      loja.dadosBancarios?.kycStatus?.toUpperCase();

    switch (status) {
      case "APPROVED":
      case "APPROVED_KYC":
      case "VALID":
      case "VERIFIED":
        return "#DDF2E8";

      case "PENDING":
      case "PENDING_KYC":
        return "#FFE6A7";

      case "REJECTED":
      case "REFUSED":
      case "BLOCKED":
        return "#FFD2D2";

      default:
        return "#E5E7EB";
    }
  };

  /* =========================================
     VERIFICA SE KYC JÁ FOI VALIDADO

     Se estiver validado, o botão
     "Validar sua conta" não aparece.
  ========================================= */

  const contaValidada = () => {
    const status =
      loja.dadosBancarios?.kycStatus?.toUpperCase();

    return (
      status === "APPROVED" ||
      status === "APPROVED_KYC" ||
      status === "VALID" ||
      status === "VERIFIED"
    );
  };

  /* =========================================
     ALTERAR STATUS DA LOJA
  ========================================= */

  const alterarStatus = async (
    novoStatus: ILoja["status"]
  ) => {
    try {
      if (novoStatus === "Inativo") {
        await desativarLoja(loja.id);

        onAtualizar();
      }
    } catch (error) {
      console.error(
        "Erro ao alterar status da loja:",
        error
      );
    }
  };

  /* =========================================
     FECHAR MENU AO CLICAR FORA
  ========================================= */

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(
          event.target as Node
        )
      ) {
        setMenuAberto(false);
      }
    }

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /* =========================================
     RENDER
  ========================================= */

  return (
    <div className={styles.lojaItem}>
      {/* =================================
          AVATAR
      ================================= */}

      <div className={styles.circle} />

      <div className={styles.containerLoja}>
        {/* =================================
            NÚMERO + MENU
        ================================= */}

        <div className={styles.containerNum}>
          <span className={styles.num}>
            #{numeroUI}
          </span>

          <div
            className={styles.menuWrapper}
            ref={menuRef}
          >
            <button
              type="button"
              className={styles.menuBtn}
              onClick={() =>
                setMenuAberto((prev) => !prev)
              }
            >
              <Ellipsis size={16} />
            </button>

            {menuAberto && (
              <div className={styles.menuPopup}>
                {/* VISUALIZAR */}

                <button
                  type="button"
                  onClick={() => {
                    navigate(
                      `/loja/${loja.id}`
                    );

                    setMenuAberto(false);
                  }}
                >
                  <Eye size={14} />

                  Visualizar
                </button>

                {/* EDITAR */}

                <button
                  type="button"
                  onClick={() => {
                    navigate(
                      `/editarloja/${loja.id}`
                    );

                    setMenuAberto(false);
                  }}
                >
                  <Pencil size={14} />

                  Editar
                </button>

                {/* ATIVAR / INATIVAR */}

                {loja.status === "Inativo" ? (
                  <button
                    type="button"
                    className={styles.ativo}
                    onClick={() => {
                      alterarStatus("Ativo");

                      setMenuAberto(false);
                    }}
                  >
                    <CheckCircle size={14} />

                    Ativar
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.inativo}
                    onClick={() => {
                      alterarStatus("Inativo");

                      setMenuAberto(false);
                    }}
                  >
                    <Ban size={14} />

                    Inativar
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* =================================
            NOME + CNPJ
        ================================= */}

        <div className={styles.nomeContainer}>
          <span className={styles.nomeLoja}>
            {loja.loja || "Loja sem nome"}
          </span>

          <span className={styles.cnpj}>
            {loja.cnpj
              ? maskCpfCnpj(loja.cnpj)
              : "CNPJ não informado"}
          </span>
        </div>

        {/* =================================
            STATUS DA LOJA + STATUS KYC
        ================================= */}

        <div className={styles.statusRow}>
          {/* STATUS DA LOJA */}

          <div className={styles.statusContainer}>
            {loja.status && (
              <span
                className={styles.status}
                style={{
                  backgroundColor:
                    getCorStatus(),
                }}
              >
                {loja.status}
              </span>
            )}
          </div>

          {/* STATUS DA CONTA */}

          <div className={styles.kycContainer}>
            <span className={styles.labelKyc}>
              Status da conta
            </span>

            <span
              className={styles.kycStatus}
              style={{
                backgroundColor:
                  getCorKycStatus(),
              }}
            >
              {getKycStatusLabel()}
            </span>
          </div>
        </div>
        {!contaValidada() && (
        <button type="button" className={styles.btnValidar} onClick={onValidarConta}>
          Validar sua conta
        </button>)}
      </div>
    </div>
  );
}